// route-kit — the two places a PORTABLE module has to hand-wave at TanStack's
// router types, in one reviewed file — plus `queryState`, the one place a
// screen reads a solid-query result for its state slots (see the bottom).
//
// WHY THEY EXIST AT ALL. TanStack types the router through a `Register`
// interface that each PORTAL declares in its own router.tsx. That is the right
// design for an app — every path and search shape is checked against the real
// route tree. It stops working the moment a feature becomes portable: a screen
// in `modules/<m>/fe` is compiled into three different shells (admin, staff,
// student) and must not know any of their route trees. With no Register in
// scope the router's generics collapse to `AnyRouter`, and two specific spots
// stop type-checking. Neither is a runtime problem — the router still validates
// paths at runtime and each route's `validateSearch` is still the authority on
// what a legal search object is.
//
// Promoted here from modules/lib (DEV-GUIDE §7.8) once CMS needed the same two
// helpers: a module importing them from another module would be exactly the
// `module → module` edge ADR-001 Fig 4.1 bans. They carry no domain word, which
// is the test for belonging in @erp/ui. `useSearchDraft` (the bottom of the
// file) joined them for the same reason: eight screens across lib, fin and cms
// carried the same debounced search box, and all eight had the same defect.
import { createEffect, createSignal, on, onCleanup, untrack } from "solid-js";
import { stringifySearchWith } from "@tanstack/solid-router";
import type { useNavigate } from "@tanstack/solid-router";

type Navigate = ReturnType<typeof useNavigate>;

/**
 * Update URL search params from a module screen.
 *
 * `navigate({ search: (prev) => … })` is typed `ParamsReducerFn<TRouter, …>` with
 * `in out` — INVARIANT — type parameters. With TRouter collapsed to AnyRouter the
 * reducer's parameter and return types no longer correspond, and invariance means
 * no annotation at the call site can satisfy it. The assertion is structural, not
 * laziness. `navigate` itself stays fully typed; only the reducer is erased.
 */
export function updateSearch<S extends Record<string, unknown>>(
  navigate: Navigate,
  update: (prev: S) => S,
): void {
  // Deliberate erasure — see above.
  void navigate({ search: update as never });
}

/**
 * Wrap a screen's dynamic import for a route's `.lazy()`. Pass the import THUNK,
 * not the promise, so the bundler still sees a static specifier and each screen
 * keeps its own chunk (Law F3).
 *
 * A screen closes its own loop with `createLazyRoute('/cms/events')`, and that
 * literal resolves against the compiling portal's concrete route tree, while the
 * route factory consuming it is generic over its parent. The two describe the
 * same runtime object; `LazyRoute` is invariant in its route type, so TS cannot
 * prove it. The route's path string must still match at runtime or the router
 * throws — nothing about that check is weakened here.
 *
 * Defaults to the module's `Route` export; a file publishing more than one route
 * (`Route` + `EditRoute`) names which with `pick`.
 */
export const lazyRoute =
  <M,>(load: () => Promise<M>, pick?: (m: M) => unknown) =>
  // Deliberate erasure — see above.
  () =>
    load().then((m) => (pick ? pick(m) : (m as { Route: unknown }).Route) as never);

/* ───────────────────────────── queryState ────────────────────────────── */
//
// WHY THIS EXISTS (audit UX-01, 2026-09-16). Every list and detail screen
// shipped a skeleton that never rendered: behind a 5s API delay the outlet was
// EMPTY for the whole wait — no heading, no bones, no error card. The
// mechanism, read from the sources rather than guessed:
//
//   · solid-query's `q.data` getter is a RESOURCE READ while the query has no
//     data yet (build/index.js: `state.data !== undefined ? resource.latest :
//     resource()`), and a resource read inside any tracked computation
//     (JSX expression, createMemo, createComputed) SUSPENDS the nearest
//     <Suspense> up the OWNER chain of the reader — `createResource`'s `read`
//     does `useContext(SuspenseContext)` at read time (solid.js, `function
//     createResource`), and only skips it for user effects (`createEffect`)
//     and event handlers, which have no `Listener`.
//   · the nearest boundary above a screen is NOT the portal's PageSkeleton
//     Suspense in app.tsx — it is the route's own: solid-router's `Match`
//     wraps every route in `<Suspense fallback={pendingComponent}>`
//     (Match.js), and no portal sets a pendingComponent, so the fallback is
//     NOTHING. The screen's DOM is detached, and the skeleton it was about to
//     render goes with it.
//   · a screen has many readers: a `createMemo` in the body (Trail's page
//     figures), a SidePanel title (`detail()?.summary`), a pager's
//     `nextCursor`, a `useQueries` options memo (locale-fields' siblings). One
//     of them outside a section's own boundary is enough — the section's
//     boundary catches nothing, the route's catches everything.
//
// THE RULE, therefore: a screen never reads `q.data` raw. It reads
// `queryState(q).data()`, which returns undefined while the query is pending
// and NEVER suspends, and it renders its own skeleton from `pending()`. The
// heading, the toolbar and the panels sit beside the section and stay
// painted; the section shows bones, then rows, then — through `error()` and
// `retry` — the ErrorCard the state slots (DataTable, ListView, StatCards,
// ErrorCard) already take. This is "Suspense-first" (TANSTACK-SOLID-GUIDE
// §3.3) made honest: the guide's own §9 sample reads `q.data?.total` OUTSIDE
// its boundary, which is exactly the blank page. Recorded as a deviation.
//
// `empty()` is the third outcome the slots need: settled, no error, nothing
// in it. The default predicate reads the shapes this repo's endpoints return
// — a bare array, a keyset page `{ data: [] }`, or null for a single record —
// and `isEmpty` overrides it for anything else (a count, a map).
//
// PLACEHOLDERS. Two kinds ride `placeholderData` in this repo, and they mean
// opposite things for the slots. `keepPreviousData` (fin, lib keyset lists)
// keeps the CURRENT page on screen while the next one loads — that is a
// refresh: rows stay, the progress bar runs. A SEEDED empty page (auth's
// registers: `prev ?? EMPTY_PAGE`, adopted so the first fetch never suspended)
// makes `isPending` false from the first render, but nothing has arrived —
// that is still the first load, and the section must show its bones, not "no
// accounts match these filters". The helper tells them apart by content: a
// placeholder that is EMPTY (per `isEmpty`) under a fetch is `pending()`; a
// placeholder with rows under a fetch is `refreshing()`. Reading `q.data` for
// that check is safe — a placeholder means the observer's data is defined, and
// the adapter's getter returns `resource.latest` without touching the boundary
// (build/index.js: `state.data !== undefined ? queryResource.latest?.data : …`).

/** The subset of a solid-query result the helper reads — structural, so `useQuery`, `useQueries[i]` and a test stub all fit. */
export interface QueryLike<TData, TError = Error> {
  /** No data yet (status `pending`) — first load, or a query that is disabled and was never fetched. */
  readonly isPending: boolean;
  /** A request is on the wire, first load or background refetch. */
  readonly isFetching: boolean;
  /** The failure, or null. */
  readonly error: TError | null;
  /** The result. In a real result this getter SUSPENDS while pending — only the helper reads it, and only once `isPending` is false. */
  readonly data: TData | undefined;
  /**
   * `data` is a placeholder (`placeholderData`), not a fetched result. Optional
   * so a stub or an older result shape still fits; a real result always has it.
   */
  readonly isPlaceholderData?: boolean;
  /** Re-run the request. */
  refetch: () => Promise<unknown>;
}

/** What `queryState` returns — one accessor per state slot, none of which suspends. */
export interface QueryState<TData, TError = Error> {
  /** First load with nothing to show — render the section's skeleton (`<TableSkeleton/>`, `<DetailSkeleton/>`, DataTable's `loading`). True while `isPending`, and while a seeded EMPTY placeholder stands in for a page still on the wire. */
  pending: () => boolean;
  /** A refetch while something is already on screen — DataTable's / ListView's `refreshing`. Never true while `pending()` is. */
  refreshing: () => boolean;
  /** The failure, or null — the `error` slot on DataTable, ListView, StatCards and ErrorCard. */
  error: () => TError | null;
  /** Settled without error and with nothing in it (see `isEmpty`) — the empty state's turn. */
  empty: () => boolean;
  /** The result, or undefined until the query has settled. Safe to read anywhere: a JSX expression, a memo, a panel title — it never suspends. */
  data: () => TData | undefined;
  /** Re-run the request — the `onRetry` slot. Returns nothing; the result lands in the accessors. */
  retry: () => void;
}

/** Tuning for `queryState` — only what "empty" means, so far. */
export interface QueryStateOptions<TData> {
  /**
   * What "nothing in it" means for this result. Default: a bare array or a
   * keyset page `{ data: [] }` with no items, or null/undefined for a single
   * record; any other object counts as something.
   */
  isEmpty?: (data: TData) => boolean;
}

const defaultIsEmpty = (data: unknown): boolean => {
  if (data === null || data === undefined) return true;
  if (Array.isArray(data)) return data.length === 0;
  if (typeof data === "object") {
    const page = (data as { data?: unknown }).data;
    if (Array.isArray(page)) return page.length === 0;
  }
  return false;
};

/**
 * Map a solid-query result to the accessors the state slots take, without ever
 * suspending. Call it once per query, beside the `useQuery`, and read the
 * accessors everywhere the screen used to read `q.data` / `q.isLoading` /
 * `q.error` / `q.refetch`:
 *
 * ```tsx
 * const list = useQuery(() => finQueries.invoicesList(search()));
 * const state = queryState(list);
 * <PageHeading title="Invoices" … />                     // paints at once
 * <DataTable rows={state.data()?.data ?? []} loading={state.pending()}
 *   refreshing={state.refreshing()} error={state.error()} onRetry={state.retry} … />
 * <CursorPager nextCursor={() => state.data()?.nextCursor} … />
 * ```
 *
 * `data()` reads `q.data` only once `q.isPending` is false, which is the whole
 * trick: solid-query's `data` getter is a resource read while pending, and a
 * resource read suspends the nearest boundary above the READER — the route's
 * own empty one, not the section's. See the file comment for the trace.
 *
 * A seeded EMPTY placeholder under a fetch (auth's `prev ?? EMPTY_PAGE`) is
 * still `pending()` — bones, not an empty state — while a kept previous page
 * (`keepPreviousData`) under a fetch is `refreshing()`. `data()` returns the
 * placeholder in both cases, as the raw result would.
 *
 * The accessors are plain functions over the result's store, so they track in
 * any reactive context exactly as `q.isPending` would.
 */
export function queryState<TData, TError = Error>(
  q: QueryLike<TData, TError>,
  options: QueryStateOptions<TData> = {},
): QueryState<TData, TError> {
  const isEmpty = options.isEmpty ?? (defaultIsEmpty as (data: TData) => boolean);
  /** A placeholder standing in for the FIRST page: empty content under a fetch (see the file comment). */
  const seededWait = () => {
    if (!q.isPlaceholderData || !q.isFetching) return false;
    const value = q.data;
    return value === undefined ? true : isEmpty(value);
  };
  const pending = () => q.isPending || seededWait();
  const error = () => (q.isPending ? null : q.error);
  const data = () => (q.isPending ? undefined : q.data);
  return {
    pending,
    refreshing: () => !pending() && q.isFetching,
    error,
    empty: () => {
      if (pending() || error() !== null) return false;
      const value = data();
      return value === undefined ? true : isEmpty(value);
    },
    data,
    retry: () => void q.refetch(),
  };
}

/* ──────────────────────────── useSearchDraft ─────────────────────────── */
//
// WHY THIS EXISTS (audit 2026-09-18, the Clear-filters finding). Eight URL-owned
// list screens (AllPatrons, Invoices, Verification, PaymentsQueue, EbookCatalog,
// PurchaseOrders, Renewals, MediaLibrary) drove their Toolbar search box from a
// local draft — `createSignal(search().search ?? "")` debounced 300ms into
// `?search=` — and every one of them seeded the draft from the URL ONCE. The
// URL owned the filter but the box did not follow it: the empty state's Clear
// filters reset the URL and the table while the box kept the stale term, and
// the next keystroke's debounce pushed `stale+char` straight back into the URL,
// silently re-filtering the list the operator had just cleared. Back/forward,
// and any link into the screen with a different `?search=`, had the same hole.
//
// THE RULE: the URL is the owner and the draft FOLLOWS it. The helper owns the
// debounce and the one effect that makes the box track the URL — a change that
// did not come from this box (Clear filters, a history step, a link) is adopted
// into the draft and cancels any keystroke still waiting to commit, because
// that keystroke was typed against a state that no longer exists and letting
// it fire would re-impose the cleared term in a shorter window.
//
// THE ECHO. The box's own commit comes back through the URL too, and a keystroke
// can land between `commit` and the router's location update (the update is
// asynchronous; a loader that awaits stretches the window). A follow effect that
// compared the URL to the draft would then reset the box to the OLDER term and
// swallow the keystroke. So the helper remembers the term it last committed —
// or last adopted — and a URL value equal to it is its own echo: ignored, and
// the draft keeps whatever the operator has typed since (a trailing space, the
// next character). Only a term the helper did not put there counts as external.

/** Wiring for `useSearchDraft` — where the URL's term is read and how a settled term is written back. */
export interface SearchDraftOptions {
  /** The URL's current term, tracked — `() => search().search`. `undefined` (no param) reads as an empty box. */
  value: () => string | undefined;
  /**
   * Write a settled term to the URL — `(term) => updateSearch(navigate, (prev) =>
   * ({ ...prev, search: term, cursor: undefined }))`. Called after the debounce
   * with the TRIMMED text, or `undefined` for an empty box so the param drops out
   * of the URL. Reset the cursor here: a keyset cursor from the previous result
   * set is meaningless against a new one.
   */
  commit: (term: string | undefined) => void;
  /** Milliseconds a keystroke waits before `commit` — the checker-queue blueprint's 300 by default. */
  delayMs?: number;
}

/** What `useSearchDraft` returns — the two things a Toolbar's search slot takes. */
export interface SearchDraft {
  /** The box's current text, untrimmed — Toolbar's `searchValue`. Follows the URL when the URL changes from outside the box. */
  value: () => string;
  /** Toolbar's `onSearch`: shows the text at once, commits it to the URL after the debounce. */
  onInput: (raw: string) => void;
}

/**
 * A debounced search box over a URL-owned `?search=` param that FOLLOWS the URL.
 * Call it once in a screen, beside `Route.useSearch()`, and bind the result to
 * the Toolbar:
 *
 * ```tsx
 * const draft = useSearchDraft({
 *   value: () => search().search,
 *   commit: (term) => updateSearch(navigate, (prev) => ({ ...prev, search: term, cursor: undefined })),
 * });
 * <Toolbar idPrefix="patrons" searchValue={draft.value()} onSearch={draft.onInput} … />
 * ```
 *
 * Typing shows at once and reaches `commit` after `delayMs`, trimmed, or as
 * `undefined` when the box is empty. When the URL's term changes through any
 * other path — the empty state's Clear filters, a history step, a link — the
 * box adopts the new term and a keystroke still waiting to commit is dropped.
 * The box's own commit echoing back through the URL is recognised and ignored,
 * so a character typed while the router was catching up survives. The screen
 * keeps reading `search().search` for the query key: the draft is only the
 * box.
 */
export function useSearchDraft(options: SearchDraftOptions): SearchDraft {
  const [draft, setDraft] = createSignal(untrack(options.value) ?? "");
  /** The term this box last wrote to the URL, or last adopted from it — the URL echoing it back is not news. */
  let known: string | undefined = untrack(options.value);
  let timer: ReturnType<typeof setTimeout> | undefined;
  const cancel = () => {
    clearTimeout(timer);
    timer = undefined;
  };
  onCleanup(cancel);

  const onInput = (raw: string) => {
    setDraft(raw);
    cancel();
    timer = setTimeout(() => {
      timer = undefined;
      const term = raw.trim() || undefined;
      known = term;
      options.commit(term);
    }, options.delayMs ?? 300);
  };

  // The URL is the owner; the box follows it — except for its own echo.
  createEffect(
    on(
      options.value,
      (term) => {
        if (term === known) return;
        known = term;
        cancel();
        setDraft(term ?? "");
      },
      { defer: true },
    ),
  );

  return { value: draft, onInput };
}

/* ─────────────────────────── the search codec ─────────────────────────── */
//
// WHY THIS EXISTS (audit UX-17, 2026-09-19). Every list screen promises "the
// URL owns the filters", and TanStack's DEFAULT codec broke the promise in
// both directions. Its stringifier re-serialises any string that JSON.parse
// would accept, so `{ trashed: "1" }` — the flag every CMS trash toggle and
// every `panelWide` write — became `?trashed=%221%22`, a JSON-quoted string
// nobody can read or type. And its parser JSON-parses every value, so a
// hand-typed or shared `?trashed=1` arrived as the NUMBER 1, failed the
// route's `v === "1"` check, and silently showed the live list.
//
// THE HOUSE RULE: primitives travel as they are typed. A string, a number or a
// boolean is written verbatim (`?trashed=1`, `?yearFrom=2020`) and read back
// as the STRING it appears as; every route's `validateSearch` already coerces
// from `unknown` (`optionalYear`, `trashedFilter`, the Zod enums), and that
// validator — not the codec — is the authority on shape (DEV-GUIDE-002 §4.1).
// Only a STRUCTURED value — an object or an array, which no route uses today
// but the router permits — is JSON-encoded, and a string that would READ as
// structured (it starts with `{`, `[` or `"`) is JSON-quoted so it round-trips.
// That last case is also the back-compat path: a bookmarked `?trashed=%221%22`
// from before this codec still decodes to "1".
//
// The WRITER rides TanStack's own `stringifySearchWith` with a parser that
// ACCEPTS structured text and THROWS on a primitive — the throw is the
// helper's documented "leave it as is" signal. The READER cannot: TanStack's
// `parseSearchWith` runs its `decode` step first, and that step (qss.ts
// `toValue`) already turns "1" into 1 and "true" into true before any parser
// sees the text — which is the defect. So `parseSearch` is written on
// URLSearchParams directly, with the same structured-or-primitive rule, and
// the test pins that the two agree. Every portal router passes both.

/** JSON-decodes a value that reads as structured (object, array, quoted string); throws on a primitive so the helpers keep the raw text. */
const parseStructured = (text: string): unknown => {
  if (/^[[{"]/.test(text)) return JSON.parse(text) as unknown;
  throw new Error("primitive");
};

/**
 * `router.parseSearch`: strips the `?`, decodes each pair, and hands a
 * string, number or boolean back AS THE STRING it was typed — only an
 * object, an array or a quoted string is JSON-decoded (text that fails to
 * decode stays text). A repeated key collects into an array, as TanStack's
 * own decoder does.
 */
export const parseSearch = (searchStr: string): Record<string, unknown> => {
  const params = new URLSearchParams(searchStr.startsWith("?") ? searchStr.slice(1) : searchStr);
  const out: Record<string, unknown> = {};
  for (const [key, raw] of params.entries()) {
    let value: unknown = raw;
    try {
      value = parseStructured(raw);
    } catch {
      /* a primitive, or structured-looking text that is not JSON — stays the text it was typed as */
    }
    const prev = out[key];
    if (prev === undefined) out[key] = value;
    else if (Array.isArray(prev)) prev.push(value);
    else out[key] = [prev, value];
  }
  return out;
};

/**
 * `router.stringifySearch`: writes primitives verbatim (`?trashed=1`), drops
 * undefined, JSON-encodes objects and arrays, and JSON-quotes a string that
 * would otherwise read as structured on the way back.
 */
export const stringifySearch = stringifySearchWith(JSON.stringify, parseStructured);
